import { Controller } from "@hotwired/stimulus"
import { createLogger } from '../utils/logger'

// Unified controller for all task execution and management actions
export default class extends Controller {
  static targets = ["executeSelectedBtn", "markSelectedDoneBtn", "removeSelectedBtn", "storyRow", "queuePanel"]
  static values = {
    buildSessionId: String,
    appProjectSlug: String,
    sessionPath: String,
    mode: String
  }
  
  connect() {
    this.logger = createLogger('task-execution')
    this.logger.info("Task execution controller connected")
    console.log("Task execution controller connected")
    this.selectedStories = new Set()
    this.initializeValues()
    this.listenForEvents()
    
    // Ensure button states are correct on connection
    this.updateButtonStates()
  }
  
  disconnect() {
    this.removeEventListeners()
  }
  
  initializeValues() {
    // Get values from URL and DOM if not provided
    if (!this.buildSessionIdValue) {
      const input = this.element.querySelector('[data-build-session-id]')
      if (input) {
        this.buildSessionIdValue = input.dataset.buildSessionId
      }
    }
    
    if (!this.appProjectSlugValue) {
      const pathParts = window.location.pathname.split('/')
      this.appProjectSlugValue = pathParts[2] // /app_projects/{slug}/build_sessions/{id}
    }
    
    if (!this.sessionPathValue) {
      this.sessionPathValue = `/app_projects/${this.appProjectSlugValue}/build_sessions/${this.buildSessionIdValue}`
    }
  }
  
  listenForEvents() {
    // Listen for custom events
    this.handleSelectionChange = this.onSelectionChange.bind(this)
    document.addEventListener('story:selection-changed', this.handleSelectionChange)
    
    // Listen for Claude command completion events
    this.handleClaudeCommandComplete = this.onClaudeCommandComplete.bind(this)
    document.addEventListener('claude:command-completed', this.handleClaudeCommandComplete)
    
    // Rails will handle broadcasting updates via Turbo Streams when models change
  }
  
  removeEventListeners() {
    document.removeEventListener('story:selection-changed', this.handleSelectionChange)
    document.removeEventListener('claude:command-completed', this.handleClaudeCommandComplete)
  }
  
  onSelectionChange(event) {
    const selectedCount = event.detail.selectedIds.length
    this.selectedStories = new Set(event.detail.selectedIds)
    
    // Enable/disable all selection-based buttons based on whether stories are selected
    if (this.hasExecuteSelectedBtnTarget) {
      this.executeSelectedBtnTarget.disabled = selectedCount === 0
    }
    if (this.hasMarkSelectedDoneBtnTarget) {
      this.markSelectedDoneBtnTarget.disabled = selectedCount === 0
    }
    if (this.hasRemoveSelectedBtnTarget) {
      this.removeSelectedBtnTarget.disabled = selectedCount === 0
    }
  }
  
  onClaudeCommandComplete(event) {
    this.logger.info("Claude command completed", event.detail)
    console.log("Claude command completed - refreshing UI components", event.detail)
    
    // Update button states since story statuses might have changed
    this.updateButtonStates()
    
    // Optionally refresh the story panel if Turbo Stream didn't already do it
    if (this.hasQueuePanelTarget && event.detail.build_session_id === this.buildSessionIdValue) {
      // The server broadcasts should handle the update, but we can trigger a refresh if needed
      this.logger.info("Story queue may need update after Claude command completion")
    }
    
    // Dispatch a custom event that other controllers can listen to
    this.dispatch('claude-command-processed', { 
      detail: {
        commandId: event.detail.command_id,
        sessionId: event.detail.session_id,
        buildSessionId: event.detail.build_session_id
      }
    })
  }
  
  // ========== Story Selection Actions ==========
  
  toggleStory(event) {
    const checkbox = event.currentTarget
    const storyId = checkbox.value
    
    // Directly update based on current checkbox state
    if (checkbox.checked) {
      this.selectedStories.add(storyId)
    } else {
      this.selectedStories.delete(storyId)
    }
    
    console.log('Toggle story:', storyId, 'checked:', checkbox.checked, 'Total selected:', this.selectedStories.size)
    
    this.broadcastSelectionChange()
  }
  
  selectStory(event) {
    // Don't select if clicking on interactive elements
    if (event.target.closest('input, button, a, .dropdown')) return
    
    const row = event.currentTarget
    const storyId = row.dataset.storyId
    
    // Visual feedback
    this.clearRowHighlights()
    row.classList.add('bg-primary/10')
    
    // Broadcast selection
    this.broadcastStorySelected(storyId)
  }
  
  // ========== Story Actions ==========
  
  editStory(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const button = event.currentTarget
    const storyId = button.dataset.storyId
    
    if (!storyId) {
      console.error('No story ID found for edit action')
      return
    }
    
    console.log('Editing story ID:', storyId)
    console.log('Session path:', this.sessionPathValue)
    
    // Construct the edit URL for build session context
    const url = `${this.sessionPathValue}/edit_story/${storyId}`
    console.log('Loading edit modal from:', url)
    
    // Try using Turbo to navigate the frame
    const turboFrame = document.getElementById('story_edit_modal')
    if (turboFrame) {
      console.log('Found turbo frame, setting src to:', url)
      
      // Add a one-time event listener to show the modal after it loads
      turboFrame.addEventListener('turbo:frame-load', () => {
        setTimeout(() => {
          const modal = document.getElementById('editStoryModal')
          if (modal && modal.showModal) {
            console.log('Showing edit modal')
            modal.showModal()
          } else {
            console.error('Modal element not found after load')
          }
        }, 100)
      }, { once: true })
      
      turboFrame.src = url
      return
    }
    
    // If no turbo frame, create one
    console.log('No turbo frame found, creating one')
    const newFrame = document.createElement('turbo-frame')
    newFrame.id = 'story_edit_modal'
    document.body.appendChild(newFrame)
    
    // Try again with the newly created frame
    this.editStory(event)
  }
  
  async startStory(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const button = event.currentTarget
    const storyId = button.dataset.storyId
    
    console.log('Starting story:', storyId)
    console.log('Session path value:', this.sessionPathValue)
    console.log('Build session ID:', this.buildSessionIdValue)
    console.log('App project slug:', this.appProjectSlugValue)
    
    // Ensure we have the session path
    if (!this.sessionPathValue) {
      if (!this.appProjectSlugValue || !this.buildSessionIdValue) {
        console.error('Missing required values for session path')
        this.showNotification('Configuration error - please refresh the page', 'error')
        return
      }
      this.sessionPathValue = `/app_projects/${this.appProjectSlugValue}/build_sessions/${this.buildSessionIdValue}`
    }
    
    // Make the request with JSON format to get the work unit prompt
    const originalHTML = button.innerHTML
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
    
    try {
      const url = `${this.sessionPathValue}/start_story`
      console.log('Requesting URL:', url)
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({ story_id: storyId })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const result = await response.json()

      if (result.success && result.work_unit_prompt) {
        console.log('Sending work unit to Claude Code')
        this.sendToClaudeCode(result.work_unit_prompt)
        this.showNotification('Story started and sent to Claude Code', 'success')

        // Remove the story row from the UI
        const storyRow = button.closest('[data-story-id]')
        if (storyRow) {
          storyRow.style.transition = 'opacity 0.3s'
          storyRow.style.opacity = '0'
          setTimeout(() => storyRow.remove(), 300)
        }

        // Update button to show it's been started
        button.innerHTML = '<i class="fas fa-check text-success"></i>'
        setTimeout(() => {
          button.innerHTML = originalHTML
          button.disabled = false
        }, 2000)
      } else {
        throw new Error(result.error || 'Failed to start story')
      }
      
    } catch (error) {
      console.error('Error starting story:', error)
      this.showNotification(`Failed to start story: ${error.message}`, 'error')
      button.innerHTML = originalHTML
      button.disabled = false
    }
  }
  
  async markAsDone(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const button = event.currentTarget
    const storyId = button.dataset.storyId
    
    await this.performAction(button, 'mark_story_done', { story_id: storyId }, {
      loadingText: 'Marking...',
      successText: 'Completed',
      errorText: 'Failed to mark as done'
      // Removed reloadOnSuccess - Turbo Stream handles updates
    })
  }
  
  async removeFromQueue(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (!confirm('Remove this story from the queue?')) return
    
    const button = event.currentTarget
    const storyId = button.dataset.storyId
    
    await this.performAction(button, 'remove_from_queue', { story_id: storyId }, {
      loadingText: 'Removing...',
      successText: 'Removed',
      errorText: 'Failed to remove'
      // Removed reloadOnSuccess - Turbo Stream handles updates
    })
  }
  
  // ========== Execution Actions ==========
  
  async clearQueue(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    if (!confirm('Are you sure you want to clear all stories from the queue?')) {
      return
    }
    
    await this.performAction(button, 'clear_queue', {}, {
      method: 'POST',
      loadingText: 'Clearing...',
      successText: 'Queue cleared',
      errorText: 'Failed to clear queue'
    })
  }
  
  async executeNext(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    await this.performAction(button, 'execute_next_story', { mode: this.modeValue }, {
      loadingText: 'Starting...',
      successText: 'Execution started',
      errorText: 'Failed to execute',
      handleClaudeCode: true,
      preferJson: true  // Need JSON format to get work_unit_prompt
    })
  }
  
  async executeSelected(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    const storyIds = Array.from(this.selectedStories)
    if (storyIds.length === 0) {
      this.showNotification('No stories selected', 'warning')
      return
    }
    
    // Force JSON response to ensure we get the work_unit_prompt
    await this.performAction(button, 'execute_selected', { 
      story_ids: storyIds,
      mode: this.modeValue 
    }, {
      method: 'POST',
      loadingText: 'Processing...',
      successText: `Starting batch execution of ${storyIds.length} stories`,
      errorText: 'Failed to execute',
      redirectOnSuccess: false,
      handleClaudeCode: true,
      preferJson: true  // Add flag to prefer JSON
    })
  }
  
  async markSelectedDone(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    const storyIds = Array.from(this.selectedStories)
    if (storyIds.length === 0) {
      this.showNotification('No stories selected', 'warning')
      return
    }
    
    await this.performAction(button, 'mark_selected_done', { 
      story_ids: storyIds
    }, {
      loadingText: 'Marking as done...',
      successText: 'Stories marked as done',
      errorText: 'Failed to mark stories as done'
      // Turbo Stream will handle the UI updates
    })
  }
  
  async removeSelected(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    const storyIds = Array.from(this.selectedStories)
    if (storyIds.length === 0) {
      this.showNotification('No stories selected', 'warning')
      return
    }
    
    await this.performAction(button, 'remove_selected_from_queue', { 
      story_ids: storyIds
    }, {
      loadingText: 'Removing from queue...',
      successText: 'Stories removed from queue',
      errorText: 'Failed to remove stories'
      // Turbo Stream will handle the UI updates
    })
  }
  
  async executeAll(event) {
    event.preventDefault()
    
    if (!confirm('Execute all pending stories? This may take a while.')) return
    
    const button = event.currentTarget
    await this.performAction(button, 'execute_all', { mode: 'auto' }, {
      loadingText: 'Starting all...',
      successText: 'All stories queued',
      errorText: 'Failed to execute all',
      handleClaudeCode: true
    })
  }
  
  async pauseExecution(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    await this.performAction(button, 'pause_execution', {}, {
      loadingText: 'Pausing...',
      successText: 'Paused',
      errorText: 'Failed to pause'
    })
  }
  
  async resumeExecution(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    await this.performAction(button, 'resume_execution', {}, {
      loadingText: 'Resuming...',
      successText: 'Resumed',
      errorText: 'Failed to resume'
    })
  }
  
  async skipNext(event) {
    event.preventDefault()
    const button = event.currentTarget
    
    await this.performAction(button, 'skip_next_story', {}, {
      loadingText: 'Skipping...',
      successText: 'Skipped',
      errorText: 'Failed to skip'
    })
  }
  
  async resetExecution(event) {
    event.preventDefault()
    
    if (!confirm('Reset execution state? This will clear all progress.')) return
    
    const button = event.currentTarget
    await this.performAction(button, 'reset_execution', {}, {
      loadingText: 'Resetting...',
      successText: 'Reset complete',
      errorText: 'Failed to reset'
    })
  }
  
  async changeMode(event) {
    const newMode = event.target.value
    this.modeValue = newMode
    
    await this.performAction(null, 'update_execution_mode', { mode: newMode }, {
      method: 'PATCH',
      skipButtonUpdate: true
    })
  }
  
  // ========== Modal Actions ==========
  
  async openStorySelectionModal(event) {
    event.preventDefault()
    const modal = document.getElementById('story-selection-modal')
    if (modal) {
      console.log('Opening story selection modal')
      console.log('Session path:', this.sessionPathValue)
      
      // Ensure we have session path
      if (!this.sessionPathValue) {
        this.initializeValues()
      }
      
      // First, update checkbox states based on current queue
      await this.refreshModalCheckboxStates()
      modal.showModal()
    } else {
      console.error('Story selection modal not found')
    }
  }
  
  async refreshModalCheckboxStates() {
    try {
      const url = `${this.sessionPathValue}/queue_status`
      console.log('Fetching queue status from:', url)
      
      // Fetch current queue state from server
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        }
      })
      
      console.log('Response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        const currentQueue = data.story_queue || []
        
        console.log('Current queue from server:', currentQueue)
        console.log('Queue contains', currentQueue.length, 'stories')
        
        // Update all checkboxes in the modal
        const checkboxes = document.querySelectorAll('#story-selection-modal .story-checkbox')
        console.log('Found checkboxes:', checkboxes.length)
        
        let checkedCount = 0
        checkboxes.forEach(checkbox => {
          const storyId = checkbox.value
          // Check if this story is in the current queue
          const isInQueue = currentQueue.includes(storyId)
          checkbox.checked = isInQueue
          
          if (isInQueue) {
            checkedCount++
            console.log(`✓ Checking story ${storyId} - found in queue`)
          }
        })
        
        console.log(`Updated ${checkedCount} checkboxes to checked state`)
        
        // Update the selected count
        this.updateModalSelectedCount()
      } else {
        console.error('Failed to fetch queue status:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Failed to refresh modal state:', error)
      console.error('Session path value:', this.sessionPathValue)
      // If we can't fetch the state, at least clear all checkboxes
      const checkboxes = document.querySelectorAll('#story-selection-modal .story-checkbox:not(:disabled)')
      checkboxes.forEach(checkbox => {
        checkbox.checked = false
      })
      this.updateModalSelectedCount()
    }
  }
  
  updateModalSelectedCount() {
    const selectedCount = document.querySelectorAll('#story-selection-modal .story-checkbox:checked:not(:disabled)').length
    const countElement = document.getElementById('selected-count')
    if (countElement) {
      countElement.textContent = selectedCount
    }
  }
  
  closeStorySelectionModal(event) {
    event.preventDefault()
    const modal = document.getElementById('story-selection-modal')
    if (modal) {
      modal.close()
    }
  }
  
  // ========== Helper Methods ==========
  
  async performAction(button, action, data = {}, options = {}) {
    const {
      method = 'POST',
      loadingText = 'Processing...',
      successText = 'Success',
      errorText = 'Failed',
      skipButtonUpdate = false,
      redirectOnSuccess = false,
      handleClaudeCode = false,
      preferJson = false
    } = options
    
    // Store original button state
    let originalHTML, originalDisabled
    if (button && !skipButtonUpdate) {
      originalHTML = button.innerHTML
      originalDisabled = button.disabled
      button.disabled = true
      button.innerHTML = `<i class="fas fa-spinner fa-spin"></i> ${loadingText}`
    }
    
    try {
      const url = `${this.sessionPathValue}/${action}`
      
      const acceptHeader = 'application/json'
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'Accept': acceptHeader
        },
        body: JSON.stringify(data)
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        // Handle JSON response
        const result = await response.json()
        
        if (result.success) {
          this.showNotification(successText, 'success')
          
          // Handle Claude Code integration
          if (handleClaudeCode && result.work_unit_prompt) {
            console.log('Got work_unit_prompt from server, sending to Claude Code')
            console.log('Work unit length:', result.work_unit_prompt.length)
            this.sendToClaudeCode(result.work_unit_prompt)
            // UI updates will come via Turbo Stream broadcasts
          } else if (handleClaudeCode) {
            console.log('handleClaudeCode is true but no work_unit_prompt in response')
            console.log('Response keys:', Object.keys(result))
          }
          
          // Handle redirect if needed
          if (redirectOnSuccess && result.claude_code_url) {
            window.location.href = result.claude_code_url
          } else if (redirectOnSuccess && result.redirect_url) {
            window.location.href = result.redirect_url
          }
          
          // Clear selections if applicable
          if (action === 'execute_selected') {
            this.clearSelections()
            // Reload the story panel to show updated statuses
            this.reloadStoryPanel()
          }
        } else {
          throw new Error(result.error || errorText)
        }
      }
      
      // Restore button state on success
      if (button && !skipButtonUpdate) {
        setTimeout(() => {
          button.innerHTML = originalHTML
          button.disabled = originalDisabled
        }, 1000)
      }
      
    } catch (error) {
      console.error(`Action ${action} failed:`, error)
      this.showNotification(`${errorText}: ${error.message}`, 'error')
      
      // Restore button state on error
      if (button && !skipButtonUpdate) {
        button.innerHTML = originalHTML
        button.disabled = originalDisabled
      }
    }
  }
  
  sendToClaudeCode(workUnitPrompt) {
    console.log('sendToClaudeCode called')
    console.log('Work unit preview:', workUnitPrompt.substring(0, 200) + '...')
    
    // Switch to Claude Code tab first if available
    const claudeTab = document.querySelector('[data-tab="claude"]')
    console.log('Claude tab found:', !!claudeTab)
    
    if (claudeTab) {
      claudeTab.click()
      console.log('Clicked Claude tab, waiting to send...')
      
      // Give time for tab to switch
      setTimeout(() => {
        this.findAndSendToClaudeCode(workUnitPrompt)
      }, 300)
    } else {
      console.log('No Claude tab, sending directly')
      this.findAndSendToClaudeCode(workUnitPrompt)
    }
  }
  
  findAndSendToClaudeCode(workUnitPrompt) {
    console.log('findAndSendToClaudeCode called')
    
    // Find all Claude Code chat components
    const allChatComponents = document.querySelectorAll('[data-controller*="claude-code-chat"]')
    console.log('Found chat components:', allChatComponents.length)
    
    let chatComponent = null
    let messageInput = null
    
    // Find visible component
    for (const component of allChatComponents) {
      if (component.style.display === 'none' || component.closest('.hidden')) continue
      
      const input = component.querySelector('[data-claude-code-chat-target*="input"]')
      if (input && input.offsetParent !== null) {
        chatComponent = component
        messageInput = input
        break
      }
    }
    
    if (chatComponent && messageInput) {
      // Insert the work unit prompt
      messageInput.value = workUnitPrompt
      messageInput.focus()
      
      // Try to submit the form
      const form = messageInput.closest('form')
      if (form) {
        const submitEvent = new SubmitEvent('submit', { 
          bubbles: true, 
          cancelable: true,
          submitter: form.querySelector('button[type="submit"]')
        })
        form.dispatchEvent(submitEvent)
      }
    } else {
      this.showNotification('Claude Code chat interface not found. Please initialize Claude Code first.', 'warning')
    }
  }
  
  clearSelections() {
    this.selectedStories.clear()
    const checkboxes = this.element.querySelectorAll('input[name="story_ids[]"]:checked')
    checkboxes.forEach(cb => cb.checked = false)
    this.broadcastSelectionChange()
  }
  
  async reloadStoryPanel() {
    console.log('Reloading story panel - refreshing page section')
    // With the SPA architecture, panel updates are handled by React state
  }
  
  clearRowHighlights() {
    this.element.querySelectorAll('tr[data-story-id]').forEach(row => {
      row.classList.remove('bg-primary/10')
    })
  }
  
  showNotification(message, type = 'info') {
    // Ensure notifications container exists
    let container = document.getElementById('notifications')
    if (!container) {
      container = document.createElement('div')
      container.id = 'notifications'
      container.className = 'fixed top-4 right-4 z-50 space-y-2'
      container.style.pointerEvents = 'none'
      document.body.appendChild(container)
    }
    
    // Create notification element
    const notification = document.createElement('div')
    notification.className = 'transform transition-all duration-300 ease-in-out translate-x-0 opacity-100'
    notification.style.pointerEvents = 'auto'
    
    const alertClass = type === 'error' ? 'alert-error' : 
                       type === 'warning' ? 'alert-warning' : 
                       type === 'success' ? 'alert-success' : 
                       'alert-info'
    
    const iconClass = type === 'error' ? 'exclamation-circle' : 
                      type === 'warning' ? 'exclamation-triangle' : 
                      type === 'success' ? 'check-circle' : 
                      'info-circle'
    
    notification.innerHTML = `
      <div class="alert ${alertClass} shadow-lg flex items-center justify-between min-w-[300px] max-w-md">
        <div class="flex items-center gap-2">
          <i class="fas fa-${iconClass}"></i>
          <span>${message}</span>
        </div>
        <button class="btn btn-ghost btn-xs" onclick="this.closest('.transform').remove()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `
    
    // Add slide-in animation
    notification.style.transform = 'translateX(400px)'
    container.appendChild(notification)
    
    // Trigger slide-in
    requestAnimationFrame(() => {
      notification.style.transform = 'translateX(0)'
    })
    
    // Auto-remove after 5 seconds with fade out
    const autoRemoveTimeout = setTimeout(() => {
      notification.style.opacity = '0'
      notification.style.transform = 'translateX(400px)'
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove()
        }
      }, 300)
    }, 5000)
    
    // Cancel auto-remove if user hovers
    notification.addEventListener('mouseenter', () => {
      clearTimeout(autoRemoveTimeout)
    })
    
    // Resume auto-remove when mouse leaves
    notification.addEventListener('mouseleave', () => {
      setTimeout(() => {
        if (notification.parentNode) {
          notification.style.opacity = '0'
          notification.style.transform = 'translateX(400px)'
          setTimeout(() => {
            if (notification.parentNode) {
              notification.remove()
            }
          }, 300)
        }
      }, 2000)
    })
  }
  
  updateButtonStates() {
    const selectedCount = this.selectedStories.size
    if (this.hasExecuteSelectedBtnTarget) {
      this.executeSelectedBtnTarget.disabled = selectedCount === 0
    }
    if (this.hasMarkSelectedDoneBtnTarget) {
      this.markSelectedDoneBtnTarget.disabled = selectedCount === 0
    }
    if (this.hasRemoveSelectedBtnTarget) {
      this.removeSelectedBtnTarget.disabled = selectedCount === 0
    }
  }
  
  broadcastSelectionChange() {
    // Update button states directly
    this.updateButtonStates()
    
    // Broadcast the event for other listeners
    const event = new CustomEvent('story:selection-changed', {
      detail: { selectedIds: Array.from(this.selectedStories) },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
  
  broadcastStorySelected(storyId) {
    const event = new CustomEvent('story:selected', {
      detail: { storyId },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
  
  csrfToken() {
    return document.querySelector('[name="csrf-token"]').content
  }
  
  // ========== Event Handlers ==========
  
  preventPropagation(event) {
    event.stopPropagation()
  }
}